import { cards } from '@portal/config/app.json';
import RolesType from '@portal/enum/rolesType';
import { CardControlEnum, ReasonType } from '@portal/models/enumerators/cards';
import {
  sanitizePasswordInput,
  validatePassword,
  type CardFeature,
} from '@portal/services/cardValidation';
import { translate } from '@portal/services/i18n';
import { hasAccess } from '@portal/utils/hasAccess';
import { notification } from 'antd';
import { useCallback, useMemo, useState } from 'react';

export enum TypeModalEnum {
  BLOCK = 'BLOCK',
  CANCEL = 'CANCEL',
  UNBLOCK = 'UNBLOCK',
  CHANGE_PASSWORD = 'CHANGE_PASSWORD',
  VIEW_PASSWORD = 'VIEW_PASSWORD',
  VIEW_CARD_DATA = 'VIEW_CARD_DATA',
  CARD_CONTROL = 'CARD_CONTROL',
  DUPLICATE = 'DUPLICATE',
}

export interface ModalState {
  type: TypeModalEnum;
  visible: boolean;
}

export interface CardControlOptions {
  feature: CardControlEnum;
  value: boolean;
}

interface PasswordsKey {
  pin: string;
  currentPassword: string;
  newPassword: string;
}

const openNotification = (message: string, type: 'warning' | 'error') => {
  notification[type]({
    message,
    placement: 'topRight',
  });
};

export const useCardPermissions = (roles: string[]) => {
  return useMemo(() => {
    const canBlock = hasAccess(roles, RolesType.CARD_BLOCK);
    const canUnblock = hasAccess(roles, RolesType.CARD_UNBLOCK);
    const canChangePassword = hasAccess(roles, RolesType.CARD_PIN_RESET);
    const canDuplicate = hasAccess(roles, RolesType.CARD_DUPLICATE_REQUEST);
    const canCancel = hasAccess(roles, RolesType.CARD_CANCEL_REQUEST);
    const canViewPassword = hasAccess(roles, RolesType.CARD_PASSWORD_VIEW);
    const canChangeLimit = hasAccess(roles, RolesType.CARD_MANAGE);
    const canRequestDuplicate = hasAccess(
      roles,
      RolesType.CARD_DUPLICATE_REQUEST
    );

    const permissionsMap: Partial<Record<CardFeature, boolean>> = {
      block: canBlock,
      unblock: canUnblock,
      change_password: canChangePassword,
      duplicate: canDuplicate,
      cancel: canCancel,
      view_password: canViewPassword,
      limit: canChangeLimit,
    };

    const hasPermissionFor = (feature: CardFeature): boolean => {
      const permission = permissionsMap[feature];
      return permission !== undefined ? permission : true;
    };

    return {
      canBlock,
      canUnblock,
      canChangePassword,
      canDuplicate,
      canCancel,
      canViewPassword,
      canChangeLimit,
      canRequestDuplicate,
      hasPermissionFor,
    };
  }, [roles]);
};

export const useCardModals = () => {
  const [modalPin, setModalPin] = useState(false);
  const [modalFirstAccess, setModalFirstAccess] = useState(false);
  const [modalTypeActions, setModalTypeActions] = useState<ModalState>({
    type: TypeModalEnum.UNBLOCK,
    visible: false,
  });
  const [isUpdateLimitModalVisible, setIsUpdateLimitModalVisible] =
    useState(false);
  const [isCardContactModalVisible, setIsCardContactModalVisible] =
    useState(false);
  const [description, setDescription] = useState(
    ReasonType.DISSATISFIED_WITH_THE_CARD
  );
  const [cardControlOptions, setCardControlOptions] =
    useState<CardControlOptions>({
      feature: CardControlEnum.CONTACTLESS,
      value: false,
    });

  const openModal = useCallback((type: TypeModalEnum) => {
    setModalTypeActions({
      type,
      visible: true,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalTypeActions((prev) => ({
      ...prev,
      visible: false,
    }));
  }, []);

  return {
    modalPin,
    modalFirstAccess,
    modalTypeActions,
    isUpdateLimitModalVisible,
    isCardContactModalVisible,
    description,
    cardControlOptions,
    setModalPin,
    setModalFirstAccess,
    setModalTypeActions,
    setIsUpdateLimitModalVisible,
    setIsCardContactModalVisible,
    setDescription,
    setCardControlOptions,
    openModal,
    closeModal,
  };
};

export const usePasswordManager = () => {
  const [password, setPassword] = useState('');
  const [passwordsKey, setPasswordsKey] = useState<PasswordsKey>({
    pin: '',
    currentPassword: '',
    newPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  const handlePasswordChange = useCallback((value: string) => {
    const sanitized = sanitizePasswordInput(value);

    if (sanitized.length === 4) {
      const validation = validatePassword(sanitized);

      if (!validation.isValid && validation.messageKey) {
        openNotification(translate(validation.messageKey), 'warning');
        return;
      }
    }

    setPassword(sanitized);
  }, []);

  const clearPasswords = useCallback(() => {
    setPassword('');
    setPasswordsKey({
      pin: '',
      currentPassword: '',
      newPassword: '',
    });
    setShowPassword(false);
    setShowCurrentPassword(false);
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const toggleCurrentPasswordVisibility = useCallback(() => {
    setShowCurrentPassword((prev) => !prev);
  }, []);

  return {
    password,
    passwordsKey,
    showPassword,
    showCurrentPassword,
    setPassword,
    setPasswordsKey,
    setShowPassword,
    setShowCurrentPassword,
    handlePasswordChange,
    clearPasswords,
    togglePasswordVisibility,
    toggleCurrentPasswordVisibility,
  };
};

export const useCardFlip = () => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);

  const toggleFlip = useCallback(() => {
    setIsFlipped((prev) => !prev);
  }, []);

  const flipToFront = useCallback(() => {
    setIsFlipped(false);
  }, []);

  const flipToBack = useCallback(() => {
    setIsFlipped(true);
  }, []);

  const startRevealing = useCallback(() => {
    setIsRevealing(true);
  }, []);

  const stopRevealing = useCallback(() => {
    setIsRevealing(false);
  }, []);

  return {
    isFlipped,
    isRevealing,
    setIsFlipped,
    setIsRevealing,
    toggleFlip,
    flipToFront,
    flipToBack,
    startRevealing,
    stopRevealing,
  };
};

export const useCardsEnabled = (roles: string[], enable: boolean) => {
  const hasAccessReadCard = hasAccess(roles, RolesType.CARD_READ);
  const isFeatureEnabled = cards?.enabled;

  return hasAccessReadCard && isFeatureEnabled && enable;
};

export const hasPermissionForFeature = (
  feature: CardFeature,
  roles: string[]
) => {
  const permissionsMap: Partial<Record<CardFeature, RolesType | null>> = {
    block: RolesType.CARD_BLOCK,
    unblock: RolesType.CARD_UNBLOCK,
    change_password: RolesType.CARD_PIN_RESET,
    duplicate: RolesType.CARD_DUPLICATE_REQUEST,
    cancel: RolesType.CARD_CANCEL_REQUEST,
    view_password: RolesType.CARD_PASSWORD_VIEW,
  };

  const neededRole = permissionsMap[feature];

  if (!neededRole) return true;

  return hasAccess(roles, neededRole);
};
